import dayjs from 'dayjs'
import { addObject, deleteObject, editObject, editObjectFilter, getObjectsWithFilter } from '../api/apiClientCrud'
import ReactPDF from '@react-pdf/renderer'
import { ENTITY_LABELS } from './constants/misc'

/** ------------------- Utilities ------------------- **/
export function capitalizeFirstLetter(str) {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const formatDocumentNumber = (number, prefix, countPrefix) => {
  const pref = countPrefix ? dayjs().format(countPrefix) : ''
  const count = String(number).padStart(4, '0')
  return `${prefix || ''}${pref}${count}`
}

export const fileToBase64 = (blob) => {
  if (!blob) return Promise.resolve('')
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result
      resolve(result ? String(result).split(',')[1] || '' : '')
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export const bufferToBlob = (buffer, mimeType = 'application/pdf') => {
  if (!buffer) return new Blob([], { type: mimeType })
  const uint8Array = new Uint8Array(buffer.data || buffer)
  return new Blob([uint8Array], { type: mimeType })
}

/** ------------------- File Upload ------------------- **/
export const handleFileUpload = async (fileUpload, user, fileType, parentType, object, setObject) => {
  if (!fileUpload || !object?.id) return null

  const formData = new FormData()
  formData.append('file', fileUpload)
  formData.append('type', fileType)
  formData.append('parentId', object.id)
  formData.append('parentType', parentType)
  formData.append('lastModBy', user.email)
  formData.append('createdBy', user.email)

  let attachments = []
  if (Array.isArray(object.attachments)) {
    attachments = object.attachments
  } else if (object.attachments && typeof object.attachments === 'object') {
    attachments = [object.attachments]
  }

  try {
    const existingFile = attachments.find((f) => f.type === fileType)

    let res
    if (existingFile?.id) {
      res = await editObject('upload', existingFile.id, formData)
    } else {
      res = await addObject('upload', formData)
    }

    if (setObject && res?.data) {
      setObject((prev) => [...(Array.isArray(prev) ? prev.filter((a) => a.type !== fileType) : []), res.data])
    }

    return res?.data
  } catch (error) {
    return null
  }
}

export const handleProductImagesUpload = async (filesUpload, user, product, setProduct) => {
  if (!filesUpload?.length || !product?.id) return null

  const formData = new FormData()
  formData.append('companyId', user.company_id)
  formData.append('parentType', 'products')
  formData.append('parentId', product.id)
  formData.append('lastModBy', user.email)
  formData.append('createdBy', user.email)
  Array.from(filesUpload).forEach((file) => formData.append('file', file))

  try {
    const res = await addObject(`products/${product.id}/images`, formData, 'POST')

    if (res?.data?.images?.length) {
      if (setProduct) {
        setProduct((prev) => {
          // On filtre pour enlever les objets de preview temporaires (ceux qui ont .file instanceof File)
          const existingRealImages = (prev.attachments || []).filter((a) => !(a.file instanceof File))

          return {
            ...prev,
            attachments: [...existingRealImages, ...res.data.images]
          }
        })
      }
      return res.data.images
    }
  } catch (error) {
    console.error('Product images upload failed:', error)
  }
  return []
}

export const handleImagesUpload = async (filesUpload, user, object, setObject, objectType) => {
  // 1. Vérification de base
  if (!filesUpload || !object?.id) return null

  // 2. On récupère le premier élément (puisque c'est un champ unique)
  const filesArray = Array.isArray(filesUpload)
    ? filesUpload
    : filesUpload instanceof FileList
      ? Array.from(filesUpload)
      : [filesUpload]

  const itemToUpload = filesArray[0]
  if (!itemToUpload) return null

  // Extraction du fichier binaire (File)
  const actualFile = itemToUpload instanceof File ? itemToUpload : itemToUpload?.file
  if (!actualFile) return null

  // 3. Préparation du FormData
  const formData = new FormData()
  formData.append('companyId', user.company_id)
  formData.append('parentType', objectType)
  formData.append('parentId', object.id)
  formData.append('lastModBy', user.email)
  formData.append('createdBy', user.email)
  formData.append('file', actualFile)

  try {
    let res
    // 4. LOGIQUE DE BASCULE PUT / POST
    // Si l'item possède un ID, c'est qu'il remplace un fichier existant
    if (itemToUpload.id) {
      res = await editObject(`${objectType}/${object.id}/images`, itemToUpload.id, formData)
    } else {
      res = await addObject(`${objectType}/${object.id}/images`, formData, 'POST')
    }

    if (res?.data?.images) {
      const newImagesFromServer = res.data.images // C'est ton tableau avec l'ID 935

      if (setObject) {
        setObject((prev) => {
          // 1. On filtre : on garde tout ce qui n'est pas une image de type 'image'
          // ET on enlève les previews temporaires.
          const otherAttachments = (prev.attachments || []).filter((a) => a.type !== 'image' && !a.isPreview)

          // 2. On fusionne les anciens fichiers (PDF, etc.) avec la nouvelle image
          return {
            ...prev,
            attachments: [...otherAttachments, ...newImagesFromServer]
          }
        })
      }
      return newImagesFromServer
    }
  } catch (error) {
    console.error('Images upload failed:', error)
  }
  return []
}

export const handleImagesDelete = async (attach, setObject) => {
  // 1. Vérification de base
  if (!attach?.id) return null

  try {
    let res = await deleteObject(`upload`, attach.id)
    if (res.status === 200) {
      if (setObject) {
        setObject((prev) => {
          // On filtre le tableau actuel pour retirer l'élément qui correspond à l'ID supprimé
          const updatedAttachments = (prev.attachments || []).filter((a) => a.id !== attach.id)

          return {
            ...prev,
            attachments: updatedAttachments
          }
        })
      }
    }
  } catch (error) {
    console.error('Images delete failed:', error)
  }
  return []
}
/** ------------------- PDF Conversion ------------------- **/

const convertPdfToBlob = async (document) => {
  const blobPdf = await ReactPDF.pdf(document)
  blobPdf.updateContainer(document)
  return await blobPdf.toBlob()
}

export const convertDocumentAndUploadFile = async (
  document,
  filename,
  user,
  fileType,
  parentType,
  object,
  setObject
) => {
  const result = await convertPdfToBlob(document)
  const file = new File([result], `${filename}.pdf`)
  try {
    await handleFileUpload(file, user, fileType, parentType, object, setObject)
  } catch {
    return true
  }
  return file
}

export const convertDocumentToBase64 = async (document, filename) => {
  const result = await convertPdfToBlob(document)
  const file = new File([result], `${filename}.pdf`)
  return await fileToBase64(file)
}

/** ------------------- Payload Builders ------------------- **/

export const buildDocumentPayload = ({ data, user, mode, dateField, extraFields = {} }) => {
  const isEdit = mode === 'edit'
  const docDate = data[dateField] ? dayjs(data[dateField]) : dayjs()

  const basePayload = { ...data, lines: data.lines ?? [] }
  const dateFields = {
    periodYear: docDate.format('YYYY'),
    periodMonth: docDate.format('MM'),
    periodYearMonth: docDate.format('YYYYMM')
  }

  if (isEdit) {
    return { ...basePayload, ...dateFields, lastModBy: user.email, ...extraFields }
  }

  return {
    ...basePayload,
    ...dateFields,
    company_id: user.company_id,
    createdBy: user.email,
    status: 0,
    ...extraFields
  }
}

export const buildInvoicePayload = ({ data, user, mode, settings, counterValue }) =>
  buildDocumentPayload({
    data,
    user,
    mode,
    dateField: 'invoice_date',
    extraFields:
      mode === 'add'
        ? {
            invoice_number: formatDocumentNumber(
              counterValue,
              settings.invoice_number_prefix,
              settings.invoice_number_countPrefix
            )
          }
        : {}
  })
export const buildOrderPayload = ({ data, user, mode, settings, counterValue }) =>
  buildDocumentPayload({
    data,
    user,
    mode,
    dateField: 'order_date',
    extraFields:
      mode === 'add'
        ? {
            order_number: formatDocumentNumber(
              counterValue,
              settings.order_number_prefix,
              settings.order_number_countPrefix
            )
          }
        : {}
  })
export const buildContractPayload = ({ data, user, mode, settings, counterValue }) =>
  buildDocumentPayload({
    data: { ...data, owner_id: user.company_id },
    user,
    mode,
    dateField: 'contract_date',
    extraFields:
      mode === 'add'
        ? {
            contract_number: formatDocumentNumber(
              counterValue,
              settings.contract_number_prefix,
              settings.contract_number_countPrefix
            )
          }
        : {}
  })

export const buildInterventionPayload = ({ data, user, mode, settings, counterValue }) =>
  buildDocumentPayload({
    data,
    user,
    mode,
    dateField: 'intervention_date',
    extraFields:
      mode === 'add'
        ? {
            intervention_number: formatDocumentNumber(
              counterValue,
              settings.intervention_number_prefix,
              settings.intervention_number_countPrefix
            )
          }
        : {}
  })

export const buildQuotePayload = ({ data, user, mode, settings, counterValue }) =>
  buildDocumentPayload({
    data,
    user,
    mode,
    dateField: 'quote_date',
    extraFields:
      mode === 'add'
        ? {
            quote_number: formatDocumentNumber(
              counterValue,
              settings.quote_number_prefix,
              settings.quote_number_countPrefix
            )
          }
        : {}
  })

export const buildCreditNotePayload = ({ data, user, mode, settings, counterValue }) =>
  buildDocumentPayload({
    data,
    user,
    mode,
    dateField: 'creditNote_date',
    extraFields:
      mode === 'add'
        ? {
            creditNote_number: formatDocumentNumber(
              counterValue,
              settings.creditNote_number_prefix,
              settings.creditNote_number_countPrefix
            )
          }
        : {}
  })

/** ------------------- Entity Creation ------------------- **/

export const createEntity = async ({
  toast,
  toastIdRef,
  history,
  user,
  entityType,
  settingsKey,
  factoryFn,
  args = [],
  redirectPath = '',
  successMessage = '',
  updateHistoryState = {},
  updateCounter = false,
  counterName,
  contractLineName,
  onSuccess,
  onError = () => {}
}) => {
  try {
    toastIdRef.current = toast.loading('Création en cours')

    const counterRes = await getObjectsWithFilter('counters', { company_id: user.company_id })
    const counters = counterRes.data.counters[0]

    let object_nb_max = user.is_superAdmin ? 0 : 5
    const contract = user.company.contracts[0]
    if (contract) object_nb_max = contract.lines.find((line) => line.module === contractLineName)?.quantity ?? 0

    const isUnlimited = object_nb_max === 0
    const canCreate = counters[counterName] <= object_nb_max || isUnlimited || !updateCounter

    if (!canCreate) {
      toast.update(toastIdRef.current, { type: 'error', render: `Compteur dépassé`, autoClose: 2000, isLoading: false })
      return
    }

    const settingsRes = await getObjectsWithFilter('settings', { company_id: user.company_id })
    const settings = settingsRes.data
    const currentNb = parseInt(settings[settingsKey] || 0, 10)

    const newEntity = factoryFn(...args, settings, user, currentNb)
    const res = await addObject(entityType, newEntity)
    if (res.status !== 201) throw new Error(`Erreur création ${entityType}`)

    const newId = res.data.id
    const newNumber = res.data[settingsKey]

    await Promise.all([
      editObjectFilter('settings', { company_id: user.company_id }, { [settingsKey]: currentNb + 1 }),
      updateCounter &&
        editObjectFilter(
          'counters',
          { company_id: user.company_id },
          { [counterName]: (counters[counterName] || 0) + 1 }
        )
    ])

    onSuccess?.(res)

    toast.update(toastIdRef.current, {
      type: 'success',
      render:
        successMessage ||
        `${ENTITY_LABELS[entityType] || entityType}${newNumber ? ` ${newNumber}` : ''} enregistré avec succès`,
      autoClose: 2000,
      isLoading: false
    })

    if (redirectPath) history(redirectPath, { state: { id: newId, ...updateHistoryState } })
  } catch (e) {
    toast.update(toastIdRef.current, {
      type: 'error',
      render: e?.response?.statusText || e.message,
      autoClose: 2000,
      isLoading: false
    })
    onError(e)
  }
}

/** ------------------- Document / Invoice / Quote Creators ------------------- **/

export const createCreditNote = (invoice, settings, user, creditNoteNb) => ({
  creditNote_date: new Date(),
  invoice_id: invoice.id,
  customer_id: invoice.customer_id,
  lines: invoice.lines.map(({ id, ...line }) => line),
  summary: `Vous trouverez ci-dessous votre avoir sur la facture n° ${invoice.invoice_number} en date du ${dayjs(
    invoice.invoice_date
  ).format('DD/MM/YYYY')}`,
  periodYear: dayjs().format('YYYY'),
  periodMonth: dayjs().format('MM'),
  periodYearMonth: dayjs().format('YYYYMM'),
  creditNote_number: formatDocumentNumber(
    creditNoteNb,
    settings.creditNote_number_prefix,
    settings.creditNote_number_countPrefix
  ),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

export const createAdjustmentInvoice = (invoice, settings, user, invoiceNb) => ({
  invoice_date: new Date(),
  quote_id: invoice.quote_id,
  customer_id: invoice.customer_id,
  lines: invoice.lines.map(({ id, ...line }) => line),
  summary: `Vous trouverez ci-dessous notre facture rectificative. Elle annule et remplace la facture n° ${
    invoice.invoice_number
  } en date du ${dayjs(invoice.invoice_date).format('DD/MM/YYYY')}`,
  total_amount_net: invoice.total_amount_net,
  periodYear: dayjs().format('YYYY'),
  periodMonth: dayjs().format('MM'),
  periodYearMonth: dayjs().format('YYYYMM'),
  invoice_number: formatDocumentNumber(invoiceNb, settings.invoice_number_prefix, settings.invoice_number_countPrefix),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

export const createInvoiceFromQuote = (quote, settings, user, invoiceNb) => ({
  invoice_date: new Date(),
  quote_id: quote.id,
  customer_id: quote.customer_id,
  lines: quote.lines.map(({ id, ...line }) => line),
  summary: `Facture selon devis ${quote.quote_number}`,
  total_amount_net: quote.total_amount_net,
  periodYear: dayjs().format('YYYY'),
  periodMonth: dayjs().format('MM'),
  periodYearMonth: dayjs().format('YYYYMM'),
  invoice_number: formatDocumentNumber(invoiceNb, settings.invoice_number_prefix, settings.invoice_number_countPrefix),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

export const createInvoice = (invoice, settings, user, invoiceNb) => ({
  invoice_date: invoice.invoice_date,
  customer_id: invoice.customer_id,
  lines: invoice.lines.map(({ id, ...line }) => line),
  summary: invoice.summary,
  total_amount_net: invoice.total_amount_net,
  periodYear: dayjs(invoice.invoice_date).format('YYYY'),
  periodMonth: dayjs(invoice.invoice_date).format('MM'),
  periodYearMonth: dayjs(invoice.invoice_date).format('YYYYMM'),
  invoice_number: formatDocumentNumber(invoiceNb, settings.invoice_number_prefix, settings.invoice_number_countPrefix),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

export const copyInvoice = (invoice, settings, user, invoiceNb) => {
  const {
    id,
    invoice_number,
    status,
    attachments,
    createdBy,
    invoice_date,
    createdDateTime,
    lastModBy,
    lastModDateTime,
    ...rest
  } = invoice

  return {
    ...rest,
    customer_id: invoice.customer_id,
    lines: invoice.lines.map(({ id, ...line }) => line),
    summary: `Copie de ${invoice.summary || 'Facture'}`,
    total_amount_net: invoice.total_amount_net,
    invoice_number: formatDocumentNumber(
      invoiceNb,
      settings.invoice_number_prefix,
      settings.invoice_number_countPrefix
    ),
    status: 0,
    company_id: user.company_id,
    createdBy: user.email
  }
}

export const copyOrder = (order, settings, user, orderNb) => {
  const {
    id,
    order_number,
    status,
    attachments,
    createdBy,
    order_date,
    createdDateTime,
    lastModBy,
    lastModDateTime,
    ...rest
  } = order

  return {
    ...rest,
    customer_id: order.customer_id,
    lines: order.lines.map(({ id, ...line }) => line),
    summary: `Copie de ${order.summary || 'Commande'}`,
    total_amount_net: order.total_amount_net,
    order_number: formatDocumentNumber(orderNb, settings.order_number_prefix, settings.order_number_countPrefix),
    status: 0,
    company_id: user.company_id,
    createdBy: user.email
  }
}

export const copyIntervention = (intervention, settings, user, interventionNb) => {
  const {
    id,
    intervention_number,
    status,
    attachments,
    createdBy,
    intervention_date,
    createdDateTime,
    lastModBy,
    lastModDateTime,
    ...rest
  } = intervention

  return {
    ...rest,
    customer_id: intervention.customer_id,
    lines: intervention.lines.map(({ id, ...line }) => line),
    summary: `Copie de ${intervention.summary || 'Intervention'}`,
    material: intervention.material,
    description: intervention.descrption,
    intervention_number: formatDocumentNumber(
      interventionNb,
      settings.intervention_number_prefix,
      settings.intervention_number_countPrefix
    ),
    status: 0,
    company_id: user.company_id,
    createdBy: user.email
  }
}

export const copyQuote = (quote, settings, user, quoteNb) => {
  const {
    id,
    quote_number,
    status,
    attachments,
    createdBy,
    quote_date,
    validity_date,
    createdDateTime,
    lastModBy,
    lastModDateTime,
    ...rest
  } = quote

  return {
    ...rest,
    customer_id: quote.customer_id,
    lines: quote.lines.map(({ id, ...line }) => line),
    summary: `Copie de ${quote.summary || 'Devis'}`,
    material: quote.material,
    description: quote.descrption,
    quote_number: formatDocumentNumber(quoteNb, settings.quote_number_prefix, settings.quote_number_countPrefix),
    status: 0,
    company_id: user.company_id,
    createdBy: user.email
  }
}

export const createQuote = (quote, settings, user, quoteNb) => ({
  invoice_date: new Date(),
  customer_id: quote.customer_id,
  lines: quote.lines.map(({ id, ...line }) => line),
  summary: quote.summary,
  description: quote.description,
  total_amount_net: quote.total_amount_net,
  periodYear: dayjs().format('YYYY'),
  periodMonth: dayjs().format('MM'),
  periodYearMonth: dayjs().format('YYYYMM'),
  quote_number: formatDocumentNumber(quoteNb, settings.quote_number_prefix, settings.quote_number_countPrefix),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

export const createInvoiceFromIntervention = (intervention, quote, settings, user, invoiceNb) => ({
  invoice_date: new Date(),
  intervention_id: intervention.id,
  customer_id: intervention.customer_id,
  lines: quote?.lines?.map(({ id, ...line }) => line) || [],
  summary: `Facture intervention ${intervention.intervention_number}`,
  quote_id: intervention.quote_id,
  total_amount_net: quote?.total_amount_net || 0,
  periodYear: dayjs().format('YYYY'),
  periodMonth: dayjs().format('MM'),
  periodYearMonth: dayjs().format('YYYYMM'),
  invoice_number: formatDocumentNumber(invoiceNb, settings.invoice_number_prefix, settings.invoice_number_countPrefix),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

export const createInvoiceFromOrder = (order, quote, settings, user, invoiceNb) => ({
  invoice_date: new Date(),
  order_id: order.id,
  customer_id: order.customer_id,
  lines: order.lines?.map(({ id, ...line }) => line) || [],
  summary: `Facture commande ${order.order_number}`,
  quote_id: order.quote_id,
  total_amount_net: quote?.total_amount_net || 0,
  periodYear: dayjs().format('YYYY'),
  periodMonth: dayjs().format('MM'),
  periodYearMonth: dayjs().format('YYYYMM'),
  invoice_number: formatDocumentNumber(invoiceNb, settings.invoice_number_prefix, settings.invoice_number_countPrefix),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

export const createInterventionFromQuote = (quote, settings, user, interNb) => ({
  intervention_date: new Date(),
  quote_id: quote.id,
  customer_id: quote.customer_id,
  lines: quote.lines.map(({ id, ...line }) => line),
  summary: `Intervention selon devis ${quote.quote_number}`,
  total_amount_net: quote.total_amount_net,
  intervention_number: formatDocumentNumber(
    interNb,
    settings.intervention_number_prefix,
    settings.intervention_number_countPrefix
  ),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

export const createIntervention = (settings, user, interNb) => ({
  intervention_number: formatDocumentNumber(
    interNb,
    settings.intervention_number_prefix,
    settings.intervention_number_countPrefix
  ),
  status: 0,
  company_id: user.company_id,
  createdBy: user.email
})

/** ------------------- Document Status ------------------- **/

export const updateDocumentStatus = async (type, document, user, toast, setIsStatusChanged) => {
  const toastId = toast.loading('Enregistrement en cours')
  try {
    await editObject(type, document.id, { ...document, lastModBy: user.email })
    setIsStatusChanged(false)
    toast.update(toastId, { type: 'success', render: 'Enregistrement terminé', autoClose: 2000, isLoading: false })
  } catch {
    toast.update(toastId, {
      type: 'error',
      render: 'Erreur lors de la mise à jour du devis',
      autoClose: 2000,
      isLoading: false
    })
  }
}

/** ------------------- Attachments ------------------- **/

export function updateAttachments(current, newFile) {
  if (Array.isArray(newFile)) return newFile

  const attachments = current || []
  const index = attachments.findIndex((f) => f.type === newFile.type && f.parentType === newFile.parentType)

  if (newFile.type === 'other' || index === -1) return [...attachments, newFile]

  const updated = [...attachments]
  updated[index] = newFile
  return updated
}
